/**
 * Hook for editing review items with optimistic updates
 * Phase 2 of Human-AI Collaborative Reviews implementation
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

interface EditReviewItemParams {
  claimId: string;
  reviewType: 'contract' | 'quantity' | 'calculation';
  itemId: string;
  judgment?: string;
  explanation?: string;
}

interface DocumentPassage {
  text: string;
  page_number?: number;
  document_id?: string;
  document_metadata_id?: string;
}

interface ReviewItem {
  item_id: string;
  title: string;
  judgment: string;
  explanation: string;
  claim_passage: DocumentPassage;
  contract_passages?: DocumentPassage[];
  original_judgment?: string;
  original_explanation?: string;
  is_ai_generated?: boolean;
  is_modified?: boolean;
  created_by?: string;
  last_modified_by?: string;
  last_modified_at?: string;
  is_deleted?: boolean;
}

interface CreateReviewItemParams {
  claimId: string;
  reviewType: 'contract' | 'quantity' | 'calculation';
  title: string;
  judgment: string;
  explanation: string;
  claim_passage?: DocumentPassage;
  contract_passages?: DocumentPassage[];
}

interface DeleteReviewItemParams {
  claimId: string;
  reviewType: 'contract' | 'quantity' | 'calculation';
  itemId: string;
}

interface EditReviewItemResponse {
  success: boolean;
  item: ReviewItem;
  message: string;
}

interface ReviewData {
  summary?: string;
  review_items?: ReviewItem[];
}

interface ReviewCacheData {
  reviewData?: ReviewData | string;
  status?: string;
  reviewId?: string;
  completedAt?: string;
  extractionNotes?: string;
}

interface UseReviewEditOptions {
  onDataRefresh?: () => void;
}

export function useReviewEdit(options: UseReviewEditOptions = {}) {
  const [editingItems, setEditingItems] = useState<Set<string>>(new Set());
  const [pendingRequests, setPendingRequests] = useState<Map<string, Promise<EditReviewItemResponse>>>(new Map());
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { onDataRefresh } = options;

  const editMutation = useMutation({
    mutationFn: async (params: EditReviewItemParams) => {
      // Check if there's already a pending request for this item
      const requestKey = `edit-${params.itemId}`;
      const existingRequest = pendingRequests.get(requestKey);
      
      if (existingRequest) {
        // Wait for the existing request to complete
        await existingRequest;
      }

      // Create new request
      const request = apiRequest(
        'PATCH',
        `/api/claims/${params.claimId}/${params.reviewType}-review/items/${params.itemId}`,
        {
          judgment: params.judgment,
          explanation: params.explanation,
        }
      ).then(async (response) => {
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to update review item');
        }
        return response.json() as Promise<EditReviewItemResponse>;
      }).finally(() => {
        // Remove from pending requests when complete
        setPendingRequests(prev => {
          const newMap = new Map(prev);
          newMap.delete(requestKey);
          return newMap;
        });
      });

      // Store the request promise
      setPendingRequests(prev => new Map(prev).set(requestKey, request));
      
      return request;
    },
    onMutate: async (params) => {
      // Add item to editing state
      setEditingItems(prev => new Set(prev).add(params.itemId));
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: [`${params.reviewType}-review`, params.claimId] 
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData([`${params.reviewType}-review`, params.claimId]);

      // Optimistically update the cache
      queryClient.setQueryData([`${params.reviewType}-review`, params.claimId], (old: ReviewCacheData | undefined) => {
        if (!old?.reviewData) return old;
        
        try {
          const reviewData: ReviewData = typeof old.reviewData === 'string' 
            ? JSON.parse(old.reviewData) 
            : old.reviewData;
          
          const updatedItems = reviewData.review_items?.map((item: ReviewItem) => {
            if (item.item_id === params.itemId) {
              const updates: Partial<ReviewItem> = { ...item };
              
              // Track original values if this is the first edit
              if (!item.is_modified) {
                if (params.judgment && params.judgment !== item.judgment) {
                  updates.original_judgment = item.judgment;
                }
                if (params.explanation && params.explanation !== item.explanation) {
                  updates.original_explanation = item.explanation;
                }
              }
              
              // Apply updates
              if (params.judgment) updates.judgment = params.judgment;
              if (params.explanation) updates.explanation = params.explanation;
              
              // Mark as modified
              updates.is_modified = true;
              updates.last_modified_at = new Date().toISOString();
              
              return updates as ReviewItem;
            }
            return item;
          }) || [];

          return {
            ...old,
            reviewData: typeof old.reviewData === 'string' 
              ? JSON.stringify({ ...reviewData, review_items: updatedItems })
              : { ...reviewData, review_items: updatedItems }
          };
        } catch (error) {
          console.error('Error updating optimistic cache:', error);
          return old;
        }
      });

      // Return a context object with the snapshotted value
      return { previousData, params };
    },
    onError: (error, params, context) => {
      // Remove from editing state
      setEditingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(params.itemId);
        return newSet;
      });

      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData([`${params.reviewType}-review`, params.claimId], context.previousData);
      }

      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    },
    onSuccess: (data, params) => {
      // Remove from editing state
      setEditingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(params.itemId);
        return newSet;
      });

      toast({
        title: "Updated Successfully",
        description: data.message,
      });

      // Call manual refresh if provided
      if (onDataRefresh) {
        onDataRefresh();
      }
    },
    onSettled: (data, error, params) => {
      // Always refetch after settled to ensure server state
      queryClient.invalidateQueries({ 
        queryKey: [`${params.reviewType}-review`, params.claimId] 
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (params: CreateReviewItemParams) => {
      const response = await apiRequest(
        'POST',
        `/api/claims/${params.claimId}/${params.reviewType}-review/items`,
        {
          title: params.title,
          judgment: params.judgment,
          explanation: params.explanation,
          claim_passage: params.claim_passage,
          contract_passages: params.contract_passages,
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create review item');
      }
      
      return response.json() as Promise<EditReviewItemResponse>;
    },
    onSuccess: (data, params) => {
      // Invalidate and refetch the review data
      queryClient.invalidateQueries({ 
        queryKey: [`${params.reviewType}-review`, params.claimId] 
      });

      toast({
        title: "Item Created",
        description: data.message,
      });

      // Call manual refresh if provided
      if (onDataRefresh) {
        onDataRefresh();
      }
    },
    onError: (error) => {
      toast({
        title: "Creation Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (params: DeleteReviewItemParams) => {
      const response = await apiRequest(
        'DELETE',
        `/api/claims/${params.claimId}/${params.reviewType}-review/items/${params.itemId}`
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete review item');
      }
      
      return response.json() as Promise<EditReviewItemResponse>;
    },
    onMutate: async (params) => {
      // Add item to editing state
      setEditingItems(prev => new Set(prev).add(params.itemId));
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: [`${params.reviewType}-review`, params.claimId] 
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData([`${params.reviewType}-review`, params.claimId]);

      // Optimistically remove the item
      queryClient.setQueryData([`${params.reviewType}-review`, params.claimId], (old: ReviewCacheData | undefined) => {
        if (!old?.reviewData) return old;
        
        try {
          const reviewData: ReviewData = typeof old.reviewData === 'string' 
            ? JSON.parse(old.reviewData) 
            : old.reviewData;
          
          const filteredItems = reviewData.review_items?.filter((item: ReviewItem) => 
            item.item_id !== params.itemId
          ) || [];

          return {
            ...old,
            reviewData: typeof old.reviewData === 'string' 
              ? JSON.stringify({ ...reviewData, review_items: filteredItems })
              : { ...reviewData, review_items: filteredItems }
          };
        } catch (error) {
          console.error('Error updating optimistic cache:', error);
          return old;
        }
      });

      return { previousData, params };
    },
    onError: (error, params, context) => {
      // Remove from editing state
      setEditingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(params.itemId);
        return newSet;
      });

      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData([`${params.reviewType}-review`, params.claimId], context.previousData);
      }

      toast({
        title: "Deletion Failed",
        description: error.message,
        variant: "destructive",
      });
    },
    onSuccess: (data, params) => {
      // Remove from editing state
      setEditingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(params.itemId);
        return newSet;
      });

      toast({
        title: "Item Deleted",
        description: data.message,
      });

      // Call manual refresh if provided
      if (onDataRefresh) {
        onDataRefresh();
      }
    },
    onSettled: (data, error, params) => {
      // Always refetch after settled to ensure server state
      queryClient.invalidateQueries({ 
        queryKey: [`${params.reviewType}-review`, params.claimId] 
      });
    },
  });

  const editItem = (params: EditReviewItemParams) => {
    editMutation.mutate(params);
  };

  const editJudgment = (claimId: string, reviewType: 'contract' | 'quantity' | 'calculation', itemId: string, judgment: string) => {
    editItem({ claimId, reviewType, itemId, judgment });
  };

  const editExplanation = (claimId: string, reviewType: 'contract' | 'quantity' | 'calculation', itemId: string, explanation: string) => {
    editItem({ claimId, reviewType, itemId, explanation });
  };

  const createItem = (params: CreateReviewItemParams) => {
    createMutation.mutate(params);
  };

  const deleteItem = (params: DeleteReviewItemParams) => {
    deleteMutation.mutate(params);
  };

  const isEditing = (itemId: string) => {
    return editingItems.has(itemId);
  };

  return {
    editItem,
    editJudgment,
    editExplanation,
    createItem,
    deleteItem,
    isEditing,
    isLoading: editMutation.isPending,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}